/**
 * The space bar reads the screen aloud (D-112).
 *
 * One key — the biggest one on the keyboard — does what the 🔊 button does:
 * start the read-aloud for the block the screen is about, or stop it if it is
 * already reading. Jeff's ask, for the iPad keyboard case and the laptop: a kid
 * who cannot find the small speaker button can thump the big bar.
 *
 * ReadAloud and Speaker register their toggle on mount, in mount order, and the
 * first one still mounted is the screen's primary reader: a screen's headline
 * block mounts before its extras (on the reveal, "why they won" registers before
 * "how it was decided"). A screen with no reader — the rumble, the collection —
 * makes the key a no-op.
 *
 * Space is taken whole. Outside a text field it never scrolls the page and never
 * clicks whatever button still has focus from the last tap: on Chrome a tapped
 * button keeps focus, so "Next ▶" then Space would have turned the page again
 * instead of reading it. Enter still activates a focused control, so keyboard
 * users lose nothing. Inside the search box a space is a space.
 */

import { isMuted } from './speech.js'

const readers = new Set()

/** Register a screen's read-aloud toggle. Returns the unregister function. */
export function registerReader(toggle) {
  readers.add(toggle)
  return () => readers.delete(toggle)
}

/** The first reader still mounted, or null. */
export function primaryReader() {
  return readers.values().next().value || null
}

const isSpace = (e) => e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space'
const inTextField = (el) =>
  !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable === true)

let installed = false

/** Installed once by App.jsx. Capture phase, so it runs before any React handler. */
export function installReadAloudKey(target = typeof window !== 'undefined' ? window : null) {
  if (installed || !target) return
  installed = true
  let held = false
  target.addEventListener(
    'keydown',
    (e) => {
      if (!isSpace(e) || e.metaKey || e.ctrlKey || e.altKey || inTextField(e.target)) return
      e.preventDefault()
      if (e.repeat || held) return // holding the bar is one press, not a stutter
      held = true
      if (isMuted()) return
      primaryReader()?.('key')
    },
    { capture: true },
  )
  // A focused button fires its click on the key UP; cancel that half too.
  target.addEventListener(
    'keyup',
    (e) => {
      if (!isSpace(e)) return
      if (held) e.preventDefault()
      held = false
    },
    { capture: true },
  )
}

export default { registerReader, primaryReader, installReadAloudKey }
